import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Невалиден линк за смяна на парола.');
  }, [token]);

  const strength = (() => {
    if (!password) return null;
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  })();

  const strengthLabel = ['Слаба', 'Средна', 'Добра', 'Силна'][strength - 1] || '';
  const strengthColor = ['bg-red-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'][strength - 1] || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Паролите не съвпадат'); return; }
    if (password.length < 8) { setError('Паролата трябва да е поне 8 символа'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Паролата е сменена успешно!');
      setTimeout(() => navigate('/admin/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Грешка при смяна на паролата');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-primary">GreenCastle</span>
          </a>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border">
          {done ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading font-bold text-xl">Паролата е сменена!</h2>
              <p className="text-muted-foreground text-sm">Пренасочване към вход...</p>
            </div>
          ) : error && !token ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="font-heading font-bold text-xl">Невалиден линк</h2>
              <p className="text-muted-foreground text-sm">Линкът е изтекъл или невалиден. Поискайте нов.</p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/admin/login')}>
                Обратно към вход
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-center mb-2">Нова парола</h1>
              <p className="text-muted-foreground text-center text-sm mb-8">Въведете новата си парола</p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label>Нова парола</Label>
                  <div className="relative mt-1">
                    <Input type={showPwd ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Минимум 8 символа" required className="pr-10" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-2 space-y-1">
                      <div className="flex gap-1">
                        {[1,2,3,4].map(i => (
                          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength ? strengthColor : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">Сила: <span className="font-medium">{strengthLabel}</span></p>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Потвърди паролата</Label>
                  <Input type="password" value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Повторете паролата" required className="mt-1" />
                  {confirm && password !== confirm && (
                    <p className="text-xs text-red-500 mt-1">Паролите не съвпадат</p>
                  )}
                </div>
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>
                )}
                <Button type="submit" className="w-full" disabled={loading || password !== confirm}>
                  {loading ? <><div className="spinner w-4 h-4 mr-2" />Запазване...</> : 'Смени паролата'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
